// ============================================
// SMS 추천 핸들러 팩토리
// ============================================
// useAiChatServer에서 SMS 관련 함수를 분리하여 관리
//
// 구조:
//   [1] 복원 유틸         — restoreSmsFromSave
//   [2] createSmsHandlers — 의존성 주입 후 발송 핸들러 반환
//
// ──────────────────────────────────────────────
// 전체 흐름 (생성 → 렌더링 → 복원)
// ──────────────────────────────────────────────
//
// 1) 생성
//    서버 에이전트(node-agent)가 문자 템플릿까지 만들어 턴 결과(smsRecommendation)로 전달
//
// 2) 렌더링
//    DisplayRenderer → smsRecommendation prop → SmsRecommendPanel
//    - bulk 모드  : BulkSmsSection (템플릿 카드 + 발송 버튼)
//    - individual : InlineSmsCell (테이블 셀 내 문자) + InlineSmsSendCell (발송 버튼)
//
// 3) 복원
//    useAiChatServer 턴 결과·세션 복원 → restoreSmsFromSave로 런타임 형식 복원
//
// ──────────────────────────────────────────────
// 모드별 데이터 구조 (smsRecommendation)
// ──────────────────────────────────────────────
//
// [bulk 모드]
//   { type, mode:'bulk', recipients:[{memberId,name,phone}],
//     templates:[{id,text,status}], individualTexts:{}, status, label, context }
//
// [individual 모드]
//   { type, mode:'individual', recipients:[{memberId,name,phone}],
//     individualTexts:{ [memberId]:{text,status} }, templates:[], status, label, context }
//
// ──────────────────────────────────────────────
// 복원 시 변환 규칙
// ──────────────────────────────────────────────
//
// restoreSmsFromSave (DB → 런타임)
//   - bulk: templates[].content → text로 역변환
//   - individual: individualTexts 그대로 복원
//   - 공통: status 그대로 유지 (sent/completed면 UI에서 발송 버튼 비활성화)
//
// ──────────────────────────────────────────────
// SMS Status 상태 지표
// ──────────────────────────────────────────────
//
// ┌────────────────┬─────────────────────────────────────────┬──────────┐
// │ Status         │ 설명                                     │ 진행상황   │
// ├────────────────┼─────────────────────────────────────────┼──────────┤
// │ loading        │ 템플릿/문자 생성 중                           │ 진행 중  │
// │ ready          │ 생성 완료, 발송 가능                          │ 준비됨   │
// │ sending        │ 발송 중                                    │ 진행 중  │
// │ sent           │ 발송 완료                                  │ 완료     │
// │ unsent         │ 발송하지 않음 (다른 템플릿 선택)                │ 건너뜀   │
// │ error          │ 생성/발송 실패                              │ 오류     │
// │ completed      │ SMS 추천 전체 프로세스 완료                   │ 완료     │
// └────────────────┴─────────────────────────────────────────┴──────────┘
//
// 상태 흐름:
//   생성: loading → ready (또는 error)
//   발송: ready → sending → sent (또는 error)
//   무시: ready → unsent
//   결과: (sent|unsent|error) → completed

import FetchModule from '../../Model/Share/Network/FetchModule';
import { TOOL_CALLING_LOG } from './aiConfig';

// ============================================
// [1] 복원 유틸 (순수 함수)
// ============================================

// DB 저장 형식 → 런타임 smsRecommendation 복원 //
export function restoreSmsFromSave(saved) {
  if (!saved) return null;

  // 기본 필드 모두 초기화
  const restored = {
    type: saved.type,
    mode: saved.mode,
    status: saved.status,
    label: saved.label,
    context: saved.context,
    recipients: [],
    individualTexts: {},
    templates: [],
  };

  if (saved.mode === 'individual') {
    // individual 모드 — 객체 형식 유지 //
    restored.recipients = saved.recipients || [];
    restored.individualTexts = saved.individualTexts || {};
  }
  else {
    // bulk 모드 — content → text 변환 //
    restored.templates = (saved.templates || []).map(t => ({
      id: t.id,
      text: t.content || t.text || '',
      status: t.status || 'ready',
    }));
    restored.recipients = saved.recipients || [];
  }

  return restored;
}

// ============================================
// [2] SMS 핸들러 팩토리
// ============================================
// useAiChatServer에서 의존성을 주입받아 핸들러 함수들을 생성
//
// 반환 함수 목록:
//   발송:  handleSmsSend, handleIndividualSmsSend

export function createSmsHandlers({
  storeName,
  chatMessagesRef,
  updateAiMessage,
}) {
  // ===== 발송 ===== //
  // 발송 content placeholder 방어 정규화 — message/send_target은 per-member 치환을 하지 않으므로 클라이언트에서 처리 //
  //   {center_name}/{store_name} → 센터명, {member_name}/{user_name} → 회원명(없으면 "회원님"). 템플릿이 실제 값을 박아넣어 보통 no-op이나, placeholder 잔존 시 그대로 발송되는 것을 차단.
  const normalizeSmsContent = (content, memberName) => {
    if (!content || typeof content !== 'string') return content || '';

    return content
      .replace(/\{center_name\}|\{store_name\}/g, storeName || '')
      .replace(/\{member_name\}|\{user_name\}/g, memberName || '회원님');
  };

  // SMS 일괄 발송 (bulk 모드) //
  const handleSmsSend = (messageId, templateId, templateText, selectedRecipients) => {
    if (TOOL_CALLING_LOG) console.log('[SMS] Send:', { messageId, templateId, templateText, recipientCount: selectedRecipients?.length });

    // 발송 중 상태로 업데이트 //
    updateAiMessage(messageId, undefined, {
      smsRecommendation: prev => ({
        ...prev,
        status: 'sending',
        templates: (prev.templates || []).map(t => ({
          ...t,
          status: t.id === templateId ? 'sending' : t.status,
        })),
      }),
    });

    // 발송 실패 → 에러 상태 //
    const setSendError = () => {
      updateAiMessage(messageId, undefined, {
        smsRecommendation: prev => ({
          ...prev,
          status: 'error',
          templates: (prev.templates || []).map(t => ({
            ...t,
            status: t.id === templateId ? 'error' : t.status,
          })),
        }),
      });
    };

    // SMS 발송 API 호출 — message/send_target (title, content, memberIds) //
    const memberIds = (selectedRecipients || []).map(r => r.memberId).filter(id => id != null);
    const fetchModule = new FetchModule();
    (
      async () => {
        try {
          const responseData = await fetchModule.postDataVersion2('message/send_target', 'POST', {
            title: storeName,
            content: normalizeSmsContent(templateText),
            memberIds,
          });

          if (responseData.status === 200) {
            // 발송 성공 → 상태 업데이트 //
            updateAiMessage(messageId, undefined, {
              smsRecommendation: prev => ({
                ...prev,
                status: 'completed',
                templates: (prev.templates || []).map(t => ({
                  ...t,
                  status: t.id === templateId ? 'sent' : 'unsent',
                })),
              }),
            });
          }
          else setSendError();
        }
        catch (error) {
          if (TOOL_CALLING_LOG) console.error('[SMS] Send error:', error);
          setSendError();
        }
      }
    )();
  };

  // SMS 개별 발송 (individual 모드) //
  const handleIndividualSmsSend = (messageId, memberId, text) => {
    if (TOOL_CALLING_LOG) console.log('[SMS] Individual send:', { messageId, memberId, text });

    // 발송 중 상태로 업데이트 //
    updateAiMessage(messageId, undefined, {
      smsRecommendation: prev => ({
        ...prev,
        individualTexts: {
          ...prev.individualTexts,
          [memberId]: { text: text || prev.individualTexts?.[memberId]?.text || '', status: 'sending' },
        },
      }),
    });

    // 발송 실패 → 에러 상태 //
    const setSendError = () => {
      updateAiMessage(messageId, undefined, {
        smsRecommendation: prev => ({
          ...prev,
          individualTexts: {
            ...prev.individualTexts,
            [memberId]: { text: text || prev.individualTexts?.[memberId]?.text || '', status: 'error' },
          },
        }),
      });
    };

    // SMS 발송 API 호출 — message/send_target (title, content, memberIds 단건) //
    const memberName = chatMessagesRef.current.find(m => m.id === messageId)?.smsRecommendation?.recipients?.find(r => r.memberId === memberId)?.name;
    const fetchModule = new FetchModule();
    (
      async () => {
        try {
          const responseData = await fetchModule.postDataVersion2('message/send_target', 'POST', {
            title: storeName,
            content: normalizeSmsContent(text, memberName),
            memberIds: [memberId],
          });

          if (responseData.status === 200) {
            // 발송 성공 → 상태 업데이트 //
            updateAiMessage(messageId, undefined, {
              smsRecommendation: prev => ({
                ...prev,
                individualTexts: {
                  ...prev.individualTexts,
                  [memberId]: { text: text || prev.individualTexts?.[memberId]?.text || '', status: 'sent' },
                },
              }),
            });
          }
          else setSendError();
        }
        catch (error) {
          if (TOOL_CALLING_LOG) console.error('[SMS] Individual send error:', error);
          setSendError();
        }
      }
    )();
  };

  return {
    // 발송 //
    handleSmsSend,
    handleIndividualSmsSend,
  };
}
